import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Zap,
  Plus,
  Edit,
  Trash2,
  Play,
  History,
  AlertCircle,
  CheckCircle,
  Bell,
  FolderPlus,
  RefreshCw,
  Settings2
} from "lucide-react";

const TRIGGER_TYPES = [
  { value: "lead_status_change", label: "Lead Status Changes", icon: RefreshCw },
  { value: "schedule_assigned", label: "Schedule Assignment", icon: Bell },
  { value: "estimate_accepted", label: "Estimate Accepted", icon: CheckCircle },
  { value: "estimate_rejected", label: "Estimate Rejected", icon: AlertCircle },
];

const ACTION_TYPES = [
  { value: "send_notification", label: "Send In-App Notification", icon: Bell },
  { value: "create_project", label: "Create Project", icon: FolderPlus },
  { value: "update_lead_status", label: "Update Lead Status", icon: RefreshCw },
];

const ROLES = [
  { value: "owner", label: "Owner" },
  { value: "project_manager", label: "Project Manager" },
  { value: "employee", label: "Employee" },
  { value: "subcontractor", label: "Subcontractor" },
];

const LEAD_STATUSES = ["new", "contacted", "qualified", "proposal", "won", "lost"];

export default function AutomationRulesPage() {
  const { user } = useContext(AuthContext);
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    trigger_type: "",
    trigger_config: {},
    action_type: "",
    action_config: {},
    is_active: true
  });

  useEffect(() => {
    fetchRules();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRules = async () => {
    try {
      const response = await axios.get(`${API}/automation-rules`);
      setRules(response.data);
    } catch (error) {
      toast.error("Failed to load automation rules");
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/automation-rules/logs?limit=50`);
      setLogs(response.data);
      setShowLogsModal(true);
    } catch (error) {
      toast.error("Failed to load logs");
    }
  };

  const handleCreateOrUpdate = async () => {
    if (!formData.name || !formData.trigger_type || !formData.action_type) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (editingRule) {
        await axios.put(`${API}/automation-rules/${editingRule.rule_id}`, formData);
        toast.success("Rule updated!");
      } else {
        await axios.post(`${API}/automation-rules`, formData);
        toast.success("Rule created!");
      }
      
      setShowCreateModal(false);
      setEditingRule(null);
      resetForm();
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save rule");
    }
  };

  const handleDelete = async (ruleId) => {
    if (!confirm("Delete this automation rule?")) return;
    
    try {
      await axios.delete(`${API}/automation-rules/${ruleId}`);
      toast.success("Rule deleted");
      fetchRules();
    } catch (error) {
      toast.error("Failed to delete rule");
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await axios.put(`${API}/automation-rules/${rule.rule_id}`, {
        is_active: !rule.is_active
      });
      toast.success(`Rule ${rule.is_active ? 'disabled' : 'enabled'}`);
      fetchRules();
    } catch (error) {
      toast.error("Failed to update rule");
    }
  };

  const handleSetupDefaults = async () => {
    try {
      const response = await axios.post(`${API}/automation-rules/setup-defaults`);
      toast.success(response.data.message);
      fetchRules();
    } catch (error) {
      toast.error("Failed to setup defaults");
    }
  };

  const openEditModal = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || "",
      trigger_type: rule.trigger_type,
      trigger_config: rule.trigger_config || {},
      action_type: rule.action_type,
      action_config: rule.action_config || {},
      is_active: rule.is_active
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      trigger_type: "",
      trigger_config: {},
      action_type: "",
      action_config: {},
      is_active: true
    });
  };

  const renderTriggerConfigForm = () => {
    if (formData.trigger_type === "lead_status_change") {
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>From Status (optional)</Label>
            <Select
              value={formData.trigger_config.from_status || "any"}
              onValueChange={(v) => setFormData({
                ...formData,
                trigger_config: { ...formData.trigger_config, from_status: v === "any" ? undefined : v }
              })}
            >
              <SelectTrigger><SelectValue placeholder="Any status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                {LEAD_STATUSES.map(s => (
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>To Status (optional)</Label>
            <Select
              value={formData.trigger_config.to_status || "any"}
              onValueChange={(v) => setFormData({
                ...formData,
                trigger_config: { ...formData.trigger_config, to_status: v === "any" ? undefined : v }
              })}
            >
              <SelectTrigger><SelectValue placeholder="Any status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                {LEAD_STATUSES.map(s => (
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderActionConfigForm = () => {
    if (formData.action_type === "send_notification") {
      return (
        <div className="space-y-4">
          <div>
            <Label>Notification Title</Label>
            <Input
              value={formData.action_config.title || ""}
              onChange={(e) => setFormData({
                ...formData,
                action_config: { ...formData.action_config, title: e.target.value }
              })}
              placeholder="e.g., Lead Status Updated"
            />
          </div>
          <div>
            <Label>Notification Body</Label>
            <Textarea
              value={formData.action_config.body || ""}
              onChange={(e) => setFormData({
                ...formData,
                action_config: { ...formData.action_config, body: e.target.value }
              })}
              placeholder="Use {lead_name}, {from_status}, {to_status} as placeholders"
              rows={2}
            />
            <p className="text-xs text-slate-500 mt-1">
              Available: {"{lead_name}"}, {"{from_status}"}, {"{to_status}"}, {"{customer_name}"}, {"{estimate_number}"}, {"{total}"}
            </p>
          </div>
          <div>
            <Label>Notify Roles</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {ROLES.map(role => (
                <label key={role.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(formData.action_config.to_roles || []).includes(role.value)}
                    onChange={(e) => {
                      const roles = formData.action_config.to_roles || [];
                      const newRoles = e.target.checked
                        ? [...roles, role.value]
                        : roles.filter(r => r !== role.value);
                      setFormData({
                        ...formData,
                        action_config: { ...formData.action_config, to_roles: newRoles }
                      });
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">{role.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      );
    }
    
    if (formData.action_type === "create_project") {
      return (
        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.action_config.copy_from_estimate !== false}
              onChange={(e) => setFormData({
                ...formData,
                action_config: { ...formData.action_config, copy_from_estimate: e.target.checked }
              })}
              className="rounded"
            />
            <span className="text-sm">Copy customer info from estimate</span>
          </label>
        </div>
      );
    }
    
    if (formData.action_type === "update_lead_status") {
      return (
        <div>
          <Label>New Status</Label>
          <Select
            value={formData.action_config.new_status || ""}
            onValueChange={(v) => setFormData({
              ...formData,
              action_config: { ...formData.action_config, new_status: v }
            })}
          >
            <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
            <SelectContent>
              {LEAD_STATUSES.map(s => (
                <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="automation-rules-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Automation Rules</h1>
          <p className="text-slate-600">Automate workflows based on events</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchLogs}>
            <History className="w-4 h-4 mr-2" />
            View Logs
          </Button>
          {rules.length === 0 && (
            <Button variant="outline" onClick={handleSetupDefaults}>
              <Settings2 className="w-4 h-4 mr-2" />
              Setup Defaults
            </Button>
          )}
          <Button onClick={() => { resetForm(); setEditingRule(null); setShowCreateModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Create Rule
          </Button>
        </div>
      </div>

      {/* Rules List */}
      {rules.length > 0 ? (
        <div className="grid gap-4">
          {rules.map(rule => {
            const trigger = TRIGGER_TYPES.find(t => t.value === rule.trigger_type);
            const action = ACTION_TYPES.find(a => a.value === rule.action_type);
            
            return (
              <Card key={rule.rule_id} className={`${!rule.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-full ${rule.is_active ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                        <Zap className={`w-5 h-5 ${rule.is_active ? 'text-indigo-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900">{rule.name}</h3>
                          <Badge variant={rule.is_active ? "default" : "secondary"}>
                            {rule.is_active ? "Active" : "Disabled"}
                          </Badge>
                        </div>
                        {rule.description && (
                          <p className="text-sm text-slate-500 mt-1">{rule.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-3 text-sm">
                          <div className="flex items-center gap-1 text-slate-600">
                            {trigger && <trigger.icon className="w-4 h-4" />}
                            <span>When: {trigger?.label || rule.trigger_type}</span>
                          </div>
                          <span className="text-slate-300">→</span>
                          <div className="flex items-center gap-1 text-slate-600">
                            {action && <action.icon className="w-4 h-4" />}
                            <span>Then: {action?.label || rule.action_type}</span>
                          </div>
                        </div>
                        {rule.trigger_count > 0 && (
                          <p className="text-xs text-slate-400 mt-2">
                            Triggered {rule.trigger_count} times • Last: {new Date(rule.last_triggered_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={rule.is_active}
                        onCheckedChange={() => handleToggleActive(rule)}
                      />
                      <Button variant="ghost" size="icon" onClick={() => openEditModal(rule)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(rule.rule_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Zap className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="font-medium text-slate-900 mb-2">No automation rules yet</h3>
            <p className="text-slate-500 mb-4">Create rules to automate your workflows</p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={handleSetupDefaults}>
                Setup Defaults
              </Button>
              <Button onClick={() => setShowCreateModal(true)}>
                Create Rule
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRule ? "Edit Rule" : "Create Automation Rule"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Rule Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Notify on estimate acceptance"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                rows={2}
              />
            </div>
            
            <div>
              <Label>Trigger (When) *</Label>
              <Select
                value={formData.trigger_type}
                onValueChange={(v) => setFormData({ ...formData, trigger_type: v, trigger_config: {} })}
              >
                <SelectTrigger><SelectValue placeholder="Select trigger" /></SelectTrigger>
                <SelectContent>
                  {TRIGGER_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="flex items-center gap-2">
                        <t.icon className="w-4 h-4" />
                        {t.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {renderTriggerConfigForm()}
            
            <div>
              <Label>Action (Then) *</Label>
              <Select
                value={formData.action_type}
                onValueChange={(v) => setFormData({ ...formData, action_type: v, action_config: {} })}
              >
                <SelectTrigger><SelectValue placeholder="Select action" /></SelectTrigger>
                <SelectContent>
                  {ACTION_TYPES.map(a => (
                    <SelectItem key={a.value} value={a.value}>
                      <div className="flex items-center gap-2">
                        <a.icon className="w-4 h-4" />
                        {a.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {renderActionConfigForm()}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateOrUpdate}>
              {editingRule ? "Update" : "Create"} Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logs Modal */}
      <Dialog open={showLogsModal} onOpenChange={setShowLogsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Automation Logs</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-2">
            {logs.length > 0 ? (
              logs.map(log => (
                <div key={log.log_id} className={`p-3 rounded-lg ${log.status === 'success' ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {log.status === 'success' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      )}
                      <span className="font-medium text-sm">{log.event_type}</span>
                      <span className="text-slate-400">→</span>
                      <span className="text-sm text-slate-600">{log.action_type}</span>
                    </div>
                    <span className="text-xs text-slate-500">
                      {new Date(log.executed_at).toLocaleString()}
                    </span>
                  </div>
                  {log.error && (
                    <p className="text-xs text-red-600 mt-1">{log.error}</p>
                  )}
                </div>
              ))
            ) : (
              <p className="text-center text-slate-500 py-8">No logs yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
